import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Modal, Popconfirm, Popover, Select, Space, Typography } from 'antd';
import { DataTable } from '../../components/DataTable';
import { StatusTag } from '../../components/StatusTag';
import type { ColumnsType } from 'antd/es/table';
import { Copy, FunnelSimple, Key, Plus } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { PageBody, PageHeader } from '../../app/AppLayout';
import { ErrorState, LoadingState } from '../../components/PageStates';
import { ApiError, NetworkError } from '../../api/client';
import { catalogApi, catalogKeys } from '../catalog/api';
import {
  ROLE_LABELS,
  staffApi,
  staffKeys,
  type CreateStaffResult,
  type StaffRoleEnum,
  type StaffStatus,
  type StaffUser,
} from './api';
import { StaffFormModal } from './StaffFormModal';
import { TemporaryPasswordModal } from './TemporaryPasswordModal';
import { tokens } from '../../theme';

const ROLE_OPTIONS = (Object.keys(ROLE_LABELS) as StaffRoleEnum[]).map((r) => ({
  value: r,
  label: ROLE_LABELS[r],
}));
const STATUS_OPTIONS: { value: StaffStatus; label: string }[] = [
  { value: 'Active', label: 'Đang làm việc' },
  { value: 'Disabled', label: 'Đã khóa' },
];
/** Giá trị riêng cho nhân viên KHÔNG gắn chi nhánh (super admin, "Toàn hệ thống"). */
const BRANCH_HQ = '__hq__';

export function StaffPage() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  const [editingStaff, setEditingStaff] = useState<StaffUser | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  /** Kết quả tạo mới hoặc đặt lại mật khẩu — mật khẩu tạm chỉ hiện đúng lần này */
  const [issued, setIssued] = useState<CreateStaffResult | null>(null);

  // Lọc client-side trên danh sách đã tải.
  const [roleFilter, setRoleFilter] = useState<StaffRoleEnum>();
  const [branchFilter, setBranchFilter] = useState<string>();
  const [statusFilter, setStatusFilter] = useState<StaffStatus>();

  const staffQuery = useQuery({ queryKey: staffKeys.all, queryFn: staffApi.list });
  const branchesQuery = useQuery({ queryKey: catalogKeys.branches, queryFn: catalogApi.listBranches });

  const branchOptions = [
    { value: BRANCH_HQ, label: 'Toàn hệ thống' },
    ...(branchesQuery.data ?? []).map((b) => ({ value: b.id, label: b.name })),
  ];

  const filteredStaff = useMemo(
    () =>
      (staffQuery.data ?? []).filter(
        (s) =>
          (!roleFilter || s.role === roleFilter) &&
          (!statusFilter || s.status === statusFilter) &&
          (!branchFilter ||
            (branchFilter === BRANCH_HQ ? s.branchId === null : s.branchId === branchFilter)),
      ),
    [staffQuery.data, roleFilter, statusFilter, branchFilter],
  );

  const resetMutation = useMutation({
    mutationFn: (id: string) => staffApi.resetPassword(id),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: staffKeys.all });
      setIssued(result);
    },
    onError: (error) => {
      message.error(
        error instanceof ApiError || error instanceof NetworkError
          ? error.message
          : 'Không đặt lại được mật khẩu.',
      );
    },
  });

  const columns: ColumnsType<StaffUser> = [
    {
      title: 'Nhân viên',
      key: 'name',
      render: (_, staff) => (
        <>
          <div style={{ fontWeight: 600 }}>{staff.name}</div>
          <div style={{ fontSize: 11.5, color: tokens.color.ink3, fontFamily: 'monospace' }}>
            {staff.username}
          </div>
        </>
      ),
    },
    {
      title: 'Vai trò',
      key: 'role',
      width: 180,
      render: (_, staff) => ROLE_LABELS[staff.role],
    },
    {
      title: 'Chi nhánh',
      key: 'branch',
      width: 180,
      // super_admin không gắn chi nhánh nào — nói rõ thay vì để ô trống trông như thiếu dữ liệu
      render: (_, staff) =>
        staff.branchName ?? <span style={{ color: tokens.color.ink3 }}>Toàn hệ thống</span>,
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 190,
      render: (_, staff) => (
        <>
          <StatusTag color={staff.status === 'Active' ? 'success' : 'default'}>
            {staff.status === 'Active' ? 'Đang làm việc' : 'Đã khóa'}
          </StatusTag>
          {staff.mustChangePassword && <StatusTag color="warning">Chưa đổi mật khẩu</StatusTag>}
        </>
      ),
    },
    {
      title: 'Đăng nhập gần nhất',
      key: 'lastLoginAt',
      width: 170,
      render: (_, staff) =>
        staff.lastLoginAt === null ? (
          <span style={{ color: tokens.color.ink3 }}>Chưa đăng nhập</span>
        ) : (
          // Giờ Việt Nam — DB lưu UTC, chỉ đổi khi hiển thị
          new Date(staff.lastLoginAt).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })
        ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 200,
      align: 'right',
      render: (_, staff) => (
        <>
          <Popconfirm
            title="Đặt lại mật khẩu?"
            description={`Mọi phiên đang mở của ${staff.name} sẽ bị cắt và họ phải dùng mật khẩu tạm mới.`}
            okText="Đặt lại"
            cancelText="Hủy"
            onConfirm={() => resetMutation.mutate(staff.id)}
          >
            <Button
              size="small"
              type="text"
              icon={<Key size={14} aria-hidden />}
              loading={resetMutation.isPending && resetMutation.variables === staff.id}
            >
              Đặt lại mật khẩu
            </Button>
          </Popconfirm>
          <Button size="small" type="text" onClick={() => setEditingStaff(staff)}>
            Sửa
          </Button>
        </>
      ),
    },
  ];

  // Gom ba bộ lọc vào MỘT nút "Lọc" trong thanh công cụ (như trang Đánh giá bữa ăn). Badge đếm số
  // bộ lọc đang thu hẹp.
  const activeFilterCount =
    (roleFilter ? 1 : 0) + (branchFilter ? 1 : 0) + (statusFilter ? 1 : 0);

  const resetFilters = () => {
    setRoleFilter(undefined);
    setBranchFilter(undefined);
    setStatusFilter(undefined);
  };

  const fieldLabel = (text: string) => (
    <span style={{ display: 'block', fontSize: 12, color: tokens.color.ink2, marginBottom: 4 }}>
      {text}
    </span>
  );

  const filterControl = (
    <Popover
      trigger="click"
      placement="bottomRight"
      content={
        <Space direction="vertical" size={12} style={{ width: 240 }}>
          <div>
            {fieldLabel('Vai trò')}
            <Select
              allowClear
              value={roleFilter}
              onChange={setRoleFilter}
              style={{ width: '100%' }}
              placeholder="Mọi vai trò"
              options={ROLE_OPTIONS}
            />
          </div>
          <div>
            {fieldLabel('Chi nhánh')}
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              value={branchFilter}
              onChange={setBranchFilter}
              style={{ width: '100%' }}
              placeholder="Mọi chi nhánh"
              options={branchOptions}
            />
          </div>
          <div>
            {fieldLabel('Trạng thái')}
            <Select
              allowClear
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              placeholder="Mọi trạng thái"
              options={STATUS_OPTIONS}
            />
          </div>
          <Button
            type="link"
            size="small"
            onClick={resetFilters}
            disabled={activeFilterCount === 0}
            style={{ padding: 0, alignSelf: 'flex-start' }}
          >
            Đặt lại bộ lọc
          </Button>
        </Space>
      }
    >
      <Badge count={activeFilterCount} size="small" offset={[-4, 2]}>
        <Button icon={<FunnelSimple size={15} aria-hidden />} size="small">
          Lọc
        </Button>
      </Badge>
    </Popover>
  );

  return (
    <>
      <PageHeader
        title="Nhân viên"
        subtitle="Tài khoản và phân quyền"
        actions={
          <Button type="primary" icon={<Plus size={14} aria-hidden />} onClick={() => setIsCreating(true)}>
            Thêm nhân viên
          </Button>
        }
      />

      <PageBody>
        <Typography.Paragraph style={{ color: tokens.color.ink2 }}>
          Thu ngân và lễ tân làm việc trên Staff App, không đăng nhập được trang này. Quản lý chi
          nhánh chỉ thấy và sửa được nhân viên thuộc chi nhánh mình.
        </Typography.Paragraph>

        {staffQuery.isPending ? (
          <LoadingState rows={6} />
        ) : staffQuery.isError ? (
          <ErrorState error={staffQuery.error} onRetry={() => void staffQuery.refetch()} />
        ) : staffQuery.data.length === 0 ? (
          <Alert
            type="info"
            showIcon
            message="Chưa có nhân viên nào"
            description="Tạo tài khoản đầu tiên. Hệ thống sinh mật khẩu tạm, nhân viên buộc đổi ở lần đăng nhập đầu."
          />
        ) : (
          <Card styles={{ body: { paddingTop: 8 } }}>
            <DataTable
              title="Danh sách nhân viên"
              subTitle={
                filteredStaff.length === staffQuery.data.length
                  ? `${staffQuery.data.length} tài khoản`
                  : `${filteredStaff.length}/${staffQuery.data.length} tài khoản`
              }
              onReload={() => void staffQuery.refetch()}
              searchPlaceholder="Tìm theo tên, tài khoản, SĐT"
              // Cột "Nhân viên" ghép tên + username nên khai thẳng chuỗi dò (heuristic đoán-cột chỉ
              // thấy `name`).
              searchText={(s) =>
                [s.name, s.username, s.phone, ROLE_LABELS[s.role], s.branchName]
                  .filter(Boolean)
                  .join(' ')
              }
              toolbarExtra={filterControl}
              rowKey="id"
              columns={columns}
              dataSource={filteredStaff}
              pagination={false}
              size="middle"
              rowClassName={(staff) => (staff.status === 'Active' ? '' : 'row-inactive')}
              locale={{ emptyText: 'Không có nhân viên nào khớp bộ lọc.' }}
            />
          </Card>
        )}
      </PageBody>

      <StaffFormModal
        staff={editingStaff}
        isCreating={isCreating}
        branches={branchesQuery.data ?? []}
        onCreated={(result) => {
          setIsCreating(false);
          setIssued(result);
        }}
        onClose={() => {
          setEditingStaff(null);
          setIsCreating(false);
        }}
      />

      <TemporaryPasswordModal issued={issued} onClose={() => setIssued(null)} />
    </>
  );
}

/** Nút sao chép dùng chung — tách ra để `TemporaryPasswordModal` cũng dùng được. */
export function CopyButton({ value, label }: { value: string; label: string }) {
  const { message } = App.useApp();

  return (
    <Button
      size="small"
      icon={<Copy size={14} aria-hidden />}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          message.success(`Đã sao chép ${label}.`);
        } catch {
          // Trình duyệt chặn clipboard (không phải HTTPS, chưa cấp quyền) —
          // người dùng vẫn đọc được giá trị trên màn hình nên không phải lỗi chặn đường
          Modal.info({
            title: `Sao chép ${label} thủ công`,
            content: <code style={{ userSelect: 'all' }}>{value}</code>,
          });
        }
      }}
    >
      Sao chép
    </Button>
  );
}
